import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { Field } from './Field.jsx';
import { inputStyle } from '../lib/ui.js';
import { ProfileSection } from './ProfileSection.jsx';
import { ReadField } from './ReadField.jsx';
import { PolicyInfoIcon } from './PolicyInfoIcon.jsx';
import { AvailabilitySection } from './AvailabilitySection.jsx';
import { TimeOffView } from './TimeOffView.jsx';
import { ManagerTORHistory } from './ManagerTORHistory.jsx';
import { SendOnboardingPaperwork } from './SendOnboardingPaperwork.jsx';
import { ActivityRows } from './ActivityRows.jsx';
import { EmploymentTimelineCard } from './EmploymentTimelineCard.jsx';
import { CoachingLogCard } from './CoachingLogCard.jsx';
import { NotificationPrefs } from './NotificationPrefs.jsx';
import { AdminCredentialReset } from './AdminCredentialReset.jsx';
import { ChangePinModal } from './ChangePinModal.jsx';
import { ChangePasswordModal } from './ChangePasswordModal.jsx';
import { ReliabilityScoreCard } from './ReliabilityScoreCard.jsx';
import { PtoHistoryTable } from './PtoHistoryTable.jsx';
import { DesiredHoursCard } from './DesiredHoursCard.jsx';

// Staff imported from Square have only a full `name`, no first/last, so the
// name editor opened blank. Pre-fill it by splitting the full name.
function editableName(p) {
  if (p.first_name || p.last_name) return { first_name: p.first_name || '', last_name: p.last_name || '' };
  const [first = '', ...rest] = String(p.name || '').trim().split(/\s+/);
  return { first_name: first, last_name: rest.join(' ') };
}

export function EmployeeProfileView({ currentUser, profileId, locations, onClose }) {
  const [profile, setProfile] = useState(null);
  const [canEdit, setCanEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [perf, setPerf] = useState(null);
  const [perfErr, setPerfErr] = useState('');
  const [activity, setActivity] = useState(null); // manager-view-only: last_login_at + chat reads
  const [editing, setEditing] = useState({}); // section -> form state
  const [saving, setSaving] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ delta: '', reason: '' });
  const [adjustErr, setAdjustErr] = useState('');
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordForm, setRecordForm] = useState({ week_start: '', hours: '', reason: '' });
  const [recordErr, setRecordErr] = useState('');
  const [history, setHistory] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [viewMode, setViewMode] = useState('self'); // 'self' | 'manager' | 'public'
  const [showChangePw, setShowChangePw] = useState(false);
  const [showChangePin, setShowChangePin] = useState(false);

  const headers = { 'x-user-id': String(currentUser.id) };

  async function load() {
    setLoading(true); setError('');
    let mode = 'public';
    try {
      const [pData, hData] = await Promise.all([
        api('GET', `/api/profile/${profileId}`, null, headers),
        api('GET', '/api/pto/me', null, { 'x-user-id': String(profileId) })
          .catch(() => ({ history: [] })),
      ]);
      setProfile(pData.profile);
      setCanEdit(pData.canEdit);
      mode = pData.viewMode || 'public';
      setViewMode(mode);
      setHistory(hData.history || []);
      // The footer needs the balance alongside the server-summed ledger so
      // it can state whether the two agree.
      setLedger(hData.ledger
        ? { ...hData.ledger, balance: Number(hData.balance ?? hData.user?.pto_balance_hours ?? 0) }
        : null);
    } catch (e) { setError(e.message || 'Failed to load profile'); }
    finally { setLoading(false); }
    // Performance: separate so a metrics failure doesn't block the profile.
    // Skip entirely for public view (staff-viewing-other) — they can't see it.
    if (mode === 'public') return;
    try {
      const r = await api('GET', `/api/profile/${profileId}/performance`, null, headers);
      setPerf(r);
    } catch (e) { setPerfErr(e.message || 'Failed to load performance'); }
    // Team activity: manager-view only (last login + chat read aggregates).
    // /api/users/:id/activity is gated server-side too; we just skip the
    // fetch for self-view to avoid a noisy 403 in console.
    if (mode === 'manager') {
      try {
        const a = await api('GET', `/api/users/${profileId}/activity`, null, headers);
        setActivity(a);
      } catch (e) { /* not critical — silently skip */ }
    }
  }
  useEffect(() => { load(); }, [profileId]);

  function startEdit(section, initial) { setEditing({ section, form: { ...initial } }); }
  function cancelEdit() { setEditing({}); }
  async function saveEdit() {
    setSaving(true);
    try {
      await api('PUT', `/api/profile/${profileId}`, editing.form, headers);
      setEditing({});
      load();
    } catch (e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  async function submitAdjust() {
    setAdjustErr('');
    try {
      const d = parseFloat(adjustForm.delta);
      if (!Number.isFinite(d) || d === 0) throw new Error('Enter a non-zero hours delta.');
      await api('POST', '/api/pto/adjust',
        { user_id: profileId, delta_hours: d, reason: adjustForm.reason || null },
        headers);
      setAdjustOpen(false); setAdjustForm({ delta: '', reason: '' });
      load();
    } catch (e) { setAdjustErr(e.message || String(e)); }
  }

  // Sunday of the current week (Empire payroll weeks run Sun–Sat).
  function currentPayrollWeekStart() {
    // Monday of the current week (payroll weeks run Mon–Sun). The server's
    // record-week endpoint rejects a non-Monday week_start, so this must
    // land on Monday. getDay(): 0=Sun..6=Sat → Sunday steps back 6 days,
    // any other day steps back to the preceding Monday.
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    const dow = d.getDay();
    d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
    return d.toISOString().slice(0, 10);
  }

  function openRecordPto() {
    setRecordErr('');
    setRecordForm({ week_start: currentPayrollWeekStart(), hours: '', reason: '' });
    setRecordOpen(true);
  }

  async function submitRecordPto() {
    setRecordErr('');
    try {
      const h = parseFloat(recordForm.hours);
      if (!Number.isFinite(h) || h <= 0) throw new Error('Enter a positive hours value.');
      if (!/^\d{4}-\d{2}-\d{2}$/.test(recordForm.week_start)) throw new Error('Pick a week.');
      await api('POST', '/api/pto/record-week', {
        employee_id: profileId,
        week_start: recordForm.week_start,
        pto_hours: h,
        reason: recordForm.reason || null,
      }, headers);
      setRecordOpen(false);
      setRecordForm({ week_start: '', hours: '', reason: '' });
      load();
    } catch (e) { setRecordErr(e.message || String(e)); }
  }

  // CJ 2026-05-16: owner-only delete on PTO history rows. Backend reverses
  // the delta on the user's balance. Used to clean up stray bad entries
  // (e.g. the "Sign-on bonus" row that turned up on Abigail's profile).
  async function deletePtoAdjustment(h) {
    const fmt = (h.delta_hours > 0 ? '+' : '') + Number(h.delta_hours).toFixed(1) + 'h';
    if (!confirm(`Delete this PTO entry?\n\n${h.type}: ${fmt}${h.reason ? ` — "${h.reason}"` : ''}\n\nThis reverses the balance change and removes the row from history.`)) return;
    try {
      const r = await api('DELETE', `/api/pto/adjustments/${h.id}`, null, headers);
      setHistory(r.history || []);
      load(); // refresh balance display
    } catch (e) { alert(e.message || 'Delete failed'); }
  }

  if (loading) return <div className="card text-sm text-gray-500">Loading profile…</div>;
  if (error)   return (
    <div>
      <button onClick={onClose} className="btn-secondary mb-3" style={{ fontSize: 12 }}>← Back to Team</button>
      <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-sm font-semibold text-red-700">Profile unavailable</div>
        <div className="text-xs text-gray-600 mt-1">{error}</div>
      </div>
    </div>
  );
  if (!profile) return null;

  const ids = (() => { try { return JSON.parse(profile.location_ids || '[]'); } catch { return []; } })();
  const locNames = ids.map((id) => locations.find((l) => l.id === id)?.name).filter(Boolean);
  const roleOpts = ['Owner', 'Manager', 'Shift Lead', 'Barista'];

  return (
    <div className="space-y-4">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={onClose} className="btn-secondary" style={{ fontSize: 12 }}>← Back to Team</button>
        <div style={{ fontSize: 13, color: '#757575' }}>Profile</div>
      </div>

      {/* Header card */}
      <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#e8f5e9', color: 'var(--forest)',
                      fontWeight: 700, fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {(profile.name || '?').slice(0, 1).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{profile.name}</div>
          {viewMode !== 'public' && (
            <div style={{ fontSize: 13, color: '#757575' }}>
              {profile.role || '—'}{locNames.length ? ` · ${locNames.join(', ')}` : ''}
            </div>
          )}
        </div>
      </div>

      {/* Contact. Email + phone are visible to all coworkers (directory).
          Hiring v3 / CJ feedback 2026-05-08 — mailing address shows in
          this card too, but ONLY for self-view + manager-view (omitted
          from PROFILE_PUBLIC_FIELDS so staff peers can't see it). */}
      <ProfileSection title="Contact" canEdit={canEdit?.contact}
        editing={editing.section === 'contact'}
        onEdit={() => startEdit('contact', {
          ...editableName(profile), preferred_name: profile.preferred_name || '',
          email: profile.email || '', phone: profile.phone || '',
          street_address: profile.street_address || '', city: profile.city || '',
          state: profile.state || '', zip_code: profile.zip_code || '',
        })}
        onCancel={cancelEdit} onSave={saveEdit} saving={saving}>
        {editing.section === 'contact' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {viewMode !== 'public' && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 6 }}>
                  Name <span style={{ fontWeight: 400, color: '#888' }}>· first / preferred / last</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
                  <Field label="First name">
                    <input value={editing.form.first_name || ''}
                      onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, first_name: e.target.value } }))}
                      style={inputStyle} maxLength={80} autoComplete="given-name" />
                  </Field>
                  <Field label="Preferred (optional)">
                    <input value={editing.form.preferred_name || ''}
                      onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, preferred_name: e.target.value } }))}
                      style={inputStyle} maxLength={80} autoComplete="nickname" placeholder="e.g. Maxie" />
                  </Field>
                  <Field label="Last name">
                    <input value={editing.form.last_name || ''}
                      onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, last_name: e.target.value } }))}
                      style={inputStyle} maxLength={80} autoComplete="family-name" />
                  </Field>
                </div>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
              <Field label="Email">
                <input value={editing.form.email || ''} onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, email: e.target.value } }))}
                  style={inputStyle} type="email" />
              </Field>
              <Field label="Phone">
                <input value={editing.form.phone || ''} onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, phone: e.target.value } }))}
                  style={inputStyle} placeholder="(555) 123-4567" />
              </Field>
            </div>
            {viewMode !== 'public' && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 6 }}>
                  Mailing address <span style={{ fontWeight: 400, color: '#888' }}>· visible only to you, managers, and admins. Used to pre-fill onboarding paperwork.</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                  <Field label="Street address">
                    <input value={editing.form.street_address || ''}
                      onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, street_address: e.target.value } }))}
                      style={inputStyle} placeholder="22 Bellevue Ave" maxLength={120} autoComplete="address-line1" />
                  </Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', gap: 8, marginTop: 8 }}>
                  <Field label="City">
                    <input value={editing.form.city || ''}
                      onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, city: e.target.value } }))}
                      style={inputStyle} maxLength={60} autoComplete="address-level2" />
                  </Field>
                  <Field label="State">
                    <input value={editing.form.state || ''}
                      onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, state: e.target.value.toUpperCase() } }))}
                      style={{ ...inputStyle, textTransform: 'uppercase' }} maxLength={2} autoComplete="address-level1" placeholder="RI" />
                  </Field>
                  <Field label="Zip">
                    <input value={editing.form.zip_code || ''}
                      onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, zip_code: e.target.value.replace(/\D/g, '').slice(0, 5) } }))}
                      style={inputStyle} maxLength={5} inputMode="numeric" autoComplete="postal-code" placeholder="02840" />
                  </Field>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {viewMode !== 'public' && (profile.first_name || profile.preferred_name || profile.last_name) && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
                {profile.first_name && <ReadField label="First name" value={profile.first_name} />}
                {profile.preferred_name && <ReadField label="Preferred" value={profile.preferred_name} />}
                {profile.last_name && <ReadField label="Last name" value={profile.last_name} />}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
              <ReadField label="Email" value={profile.email} />
              <ReadField label="Phone" value={profile.phone} />
            </div>
            {viewMode !== 'public' && (profile.street_address || profile.city || profile.state || profile.zip_code) && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase',
                                letterSpacing: '0.06em', marginBottom: 4 }}>
                  Mailing address
                </div>
                <div style={{ fontSize: 14, color: '#212121' }}>
                  {profile.street_address && <div>{profile.street_address}</div>}
                  <div>
                    {[profile.city, profile.state].filter(Boolean).join(', ')}
                    {profile.zip_code ? ` ${profile.zip_code}` : ''}
                  </div>
                </div>
              </div>
            )}
            {viewMode !== 'public' && !(profile.street_address || profile.city || profile.state || profile.zip_code) && (
              <div style={{ fontSize: 12, color: '#888' }}>
                <em>No mailing address on file.</em> Tap Edit to add — used to pre-fill onboarding paperwork.
              </div>
            )}
          </div>
        )}
      </ProfileSection>

      {/* Google Appointment Schedule URL moved to the Hiring tab
          (CJ 2026-05-21) so managers don't tab-switch to update it. */}

      {/* Account administration — security + notifications merged.
          Self profile only; managers see admin reset under the same
          header. R44: moved lower, just above Reliability. */}

      {/* Emergency — hidden for staff-viewing-other (public mode). */}
      {viewMode !== 'public' && <ProfileSection title="Emergency contact" canEdit={canEdit?.emergency}
        editing={editing.section === 'emergency'}
        onEdit={() => startEdit('emergency', {
          emergency_contact_name: profile.emergency_contact_name || '',
          emergency_contact_phone: profile.emergency_contact_phone || '',
          emergency_contact_relationship: profile.emergency_contact_relationship || '',
        })}
        onCancel={cancelEdit} onSave={saveEdit} saving={saving}>
        {editing.section === 'emergency' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
            <Field label="Name"><input value={editing.form.emergency_contact_name || ''}
              onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, emergency_contact_name: e.target.value } }))}
              style={inputStyle} /></Field>
            <Field label="Phone"><input value={editing.form.emergency_contact_phone || ''}
              onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, emergency_contact_phone: e.target.value } }))}
              style={inputStyle} /></Field>
            <Field label="Relationship"><input value={editing.form.emergency_contact_relationship || ''}
              onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, emergency_contact_relationship: e.target.value } }))}
              style={inputStyle} placeholder="e.g. spouse, parent" /></Field>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
            <ReadField label="Name" value={profile.emergency_contact_name} />
            <ReadField label="Phone" value={profile.emergency_contact_phone} />
            <ReadField label="Relationship" value={profile.emergency_contact_relationship} />
          </div>
        )}
      </ProfileSection>}

      {/* New hire paperwork — managers and owners only (CJ 2026-08-08). Sits
          above Employment because sending it is a day-one task, and the
          component returns null for anyone who isn't a manager viewing
          someone else. */}
      <SendOnboardingPaperwork userId={profile.id} userName={profile.name}
        currentUser={currentUser} viewMode={viewMode} />

      {/* Employment — last day (self-service + manager). Feeds Availability
          Health: staff drop from the coverage projection after this date. */}
      {viewMode !== 'public' && <ProfileSection title="Employment" canEdit={canEdit?.employment}
        editing={editing.section === 'employment'}
        onEdit={() => startEdit('employment', { last_day: profile.last_day || '' })}
        onCancel={cancelEdit} onSave={saveEdit} saving={saving}>
        {editing.section === 'employment' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
            <Field label="Last day (if known)">
              <input type="date" value={editing.form.last_day || ''}
                onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, last_day: e.target.value } }))}
                style={inputStyle} />
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Leave blank if you don't know yet. Helps us plan staffing before you go.</div>
            </Field>
            <Field label="Reason (if leaving)">
              <input list="last-day-reasons" value={editing.form.last_day_reason || ''}
                onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, last_day_reason: e.target.value } }))}
                placeholder="e.g. found another job" style={inputStyle} />
              <datalist id="last-day-reasons">
                <option value="Found another job" />
                <option value="Leaving for school" />
                <option value="End of summer / seasonal" />
                <option value="Moving" />
                <option value="Personal" />
              </datalist>
            </Field>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
            <ReadField label="Last day" value={profile.last_day || '—'} />
            <ReadField label="Reason" value={profile.last_day_reason || '—'} />
          </div>
        )}
      </ProfileSection>}

      {viewMode !== 'public' && <DesiredHoursCard profile={profile} canEdit={canEdit?.hours}
        isSelf={viewMode === 'self'} headers={headers} onSaved={load} />}

      {/* Role + locations — hidden in public view too. */}
      {viewMode !== 'public' && (canEdit?.role || canEdit?.locations || profile.role) && (
        <ProfileSection title="Role & locations" canEdit={canEdit?.role || canEdit?.locations}
          editing={editing.section === 'rolelocs'}
          onEdit={() => startEdit('rolelocs', {
            role: profile.role || 'Barista',
            location_ids: ids.slice(),
            primary_location_id: profile.primary_location_id || '',
          })}
          onCancel={cancelEdit} onSave={saveEdit} saving={saving}>
          {editing.section === 'rolelocs' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Field label="Role">
                <select value={editing.form.role}
                  onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, role: e.target.value } }))}
                  style={inputStyle}>
                  {roleOpts.map((r) => <option key={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Locations">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {locations.map((l) => {
                    const checked = (editing.form.location_ids || []).includes(l.id);
                    return (
                      <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                                                  border: `1px solid ${checked ? 'var(--forest)' : '#d0d4d9'}`,
                                                  background: checked ? 'rgba(45,122,58,0.08)' : '#fff',
                                                  borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                        <input type="checkbox" checked={checked}
                          onChange={(e) => setEditing((s) => ({
                            ...s,
                            form: {
                              ...s.form,
                              location_ids: e.target.checked
                                ? [...(s.form.location_ids || []), l.id]
                                : (s.form.location_ids || []).filter((x) => x !== l.id),
                            },
                          }))} />
                        {l.name}
                      </label>
                    );
                  })}
                </div>
              </Field>
              <Field label="Primary location (default landing)">
                <select value={editing.form.primary_location_id || ''}
                  onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, primary_location_id: e.target.value ? Number(e.target.value) : null } }))}
                  style={inputStyle}>
                  <option value="">— Use first assigned —</option>
                  {locations
                    .filter((l) => (editing.form.location_ids || []).includes(l.id))
                    .map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </Field>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
              <ReadField label="Role" value={profile.role} />
              <ReadField label="Locations" value={locNames.length ? locNames.join(', ') : null} />
              <ReadField label="Primary location" value={
                profile.primary_location_id
                  ? (locations.find((l) => l.id === profile.primary_location_id)?.name || null)
                  : null
              } />
            </div>
          )}
        </ProfileSection>
      )}

      {/* Pay & PTO */}
      {viewMode !== 'public' && (canEdit?.pay || canEdit?.pto || profile.pto_balance_hours != null) && (
        <ProfileSection
          title={<>Pay &amp; PTO <PolicyInfoIcon label="PTO policy" text="To be used for sick time or pre-approved vacation time. Accrued balances are paid out to any employee with 1+ year service." /></>}
          canEdit={canEdit?.pay}
          editing={editing.section === 'pay'}
          onEdit={() => startEdit('pay', { hourly_rate: profile.hourly_rate ?? '' })}
          onCancel={cancelEdit} onSave={saveEdit} saving={saving}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
            {editing.section === 'pay' ? (
              <Field label="Hourly rate (USD)">
                <input value={editing.form.hourly_rate}
                  onChange={(e) => setEditing((s) => ({ ...s, form: { ...s.form, hourly_rate: e.target.value } }))}
                  style={inputStyle} type="number" step="0.25" placeholder="15.00" />
              </Field>
            ) : (
              <ReadField label="Hourly rate" value={profile.hourly_rate != null ? `$${Number(profile.hourly_rate).toFixed(2)}` : null} />
            )}
            <ReadField label="PTO balance" value={profile.pto_balance_hours != null ? `${Number(profile.pto_balance_hours).toFixed(1)} h` : null} />
            <ReadField label="Accrual rate"
              value={profile.pto_accrual_rate ? `1 hr / ${Math.round(1 / profile.pto_accrual_rate)} worked` : null} />
          </div>
          {(canEdit?.pto || window.EmpireRoles.hasCapability(currentUser, 'review_tor'))
            && !adjustOpen && !recordOpen && editing.section !== 'pay' && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {window.EmpireRoles.hasCapability(currentUser, 'review_tor') && (
                <button onClick={openRecordPto}
                  className="btn-secondary" style={{ fontSize: 12 }}>
                  Record PTO used
                </button>
              )}
              {canEdit?.pto && (
                <button onClick={() => { setAdjustOpen(true); setAdjustErr(''); setAdjustForm({ delta: '', reason: '' }); }}
                  className="btn-secondary" style={{ fontSize: 12 }}>
                  Adjust PTO balance
                </button>
              )}
            </div>
          )}
          {recordOpen && (
            <div style={{ marginTop: 12, padding: 12, border: '1px solid #d0d4d9', borderRadius: 8, background: '#fafbfc' }}>
              <div style={{ fontSize: 12, color: '#757575', marginBottom: 8 }}>
                Record PTO that {profile.name?.split(' ')[0] || 'this employee'} used but didn't submit through Monarch.
                Deducts from balance and adds PTO pay to the selected week's payroll.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end' }}>
                <div>
                  <div style={{ fontSize: 11, color: '#757575', marginBottom: 2 }}>Week (Sun start)</div>
                  <input type="date" value={recordForm.week_start}
                    onChange={(e) => setRecordForm((f) => ({ ...f, week_start: e.target.value }))}
                    style={{ ...inputStyle, width: 150 }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#757575', marginBottom: 2 }}>Hours</div>
                  <input type="number" step="0.25" min="0.25" placeholder="8" value={recordForm.hours}
                    onChange={(e) => setRecordForm((f) => ({ ...f, hours: e.target.value }))}
                    style={{ ...inputStyle, width: 100 }} />
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: 11, color: '#757575', marginBottom: 2 }}>Reason (optional)</div>
                  <input placeholder="e.g. legacy request pre-Monarch" value={recordForm.reason}
                    onChange={(e) => setRecordForm((f) => ({ ...f, reason: e.target.value }))}
                    style={inputStyle} />
                </div>
              </div>
              {recordErr && <div style={{ fontSize: 11, color: '#c62828', marginTop: 6 }}>{recordErr}</div>}
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button onClick={submitRecordPto}
                  style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Record PTO used
                </button>
                <button onClick={() => setRecordOpen(false)} className="btn-secondary" style={{ fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}
          {adjustOpen && (
            <div style={{ marginTop: 12, padding: 12, border: '1px solid #d0d4d9', borderRadius: 8, background: '#fafbfc' }}>
              <div style={{ fontSize: 12, color: '#757575', marginBottom: 8 }}>
                Positive credits, negative deducts. Logged in the audit trail below.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input type="number" step="0.5" placeholder="Hours (+/-)" value={adjustForm.delta}
                  onChange={(e) => setAdjustForm((f) => ({ ...f, delta: e.target.value }))}
                  style={{ ...inputStyle, flex: '0 0 140px' }} />
                <input placeholder="Reason (optional)" value={adjustForm.reason}
                  onChange={(e) => setAdjustForm((f) => ({ ...f, reason: e.target.value }))}
                  style={{ ...inputStyle, flex: 1, minWidth: 180 }} />
              </div>
              {adjustErr && <div style={{ fontSize: 11, color: '#c62828', marginTop: 6 }}>{adjustErr}</div>}
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button onClick={submitAdjust}
                  style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Save adjustment
                </button>
                <button onClick={() => setAdjustOpen(false)} className="btn-secondary" style={{ fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}
          <PtoHistoryTable history={history} currentUser={currentUser}
            onDelete={deletePtoAdjustment} ledger={ledger} />
        </ProfileSection>
      )}

      {/* Schedule preferences — Availability + Time Off requests live
          together since both shape the upcoming schedule. R45 merge. */}
      {viewMode !== 'public' && (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '14px 16px 8px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#212121' }}>Schedule preferences</div>
            <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 2 }}>
              Hours you're available for shifts plus any time off you've requested.
            </div>
          </div>
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <AvailabilitySection
              currentUser={currentUser}
              targetUserId={profileId}
              canEdit={canEdit?.contact}
              embedded
            />
            {viewMode === 'self' && (
              <TimeOffView currentUser={currentUser} isManager={false} />
            )}
            {viewMode === 'manager' && (
              <ManagerTORHistory currentUser={currentUser} targetUserId={profileId} />
            )}
          </div>
        </div>
      )}


      {/* Onboarding documents are sent only from the Hiring screen
          (post-pass / post-hire flow), not from the team profile.
          CJ feedback 2026-05-09: keep the document send flow scoped
          to the moment of hire so managers don't accidentally email
          a tenured employee a fresh W-4 from their profile page. */}

      {/* Employment timeline — manager + owner can see/edit this for any
          team member (including themselves, for hire-date corrections).
          CJ feedback 2026-05-08. */}
      {viewMode !== 'public' && (
        <EmploymentTimelineCard currentUser={currentUser} targetUserId={profileId} targetName={profile?.name} />
      )}

      {/* Coaching log — manager view only. NEVER shown in self-view
          even for owners (the log is "improvement conversations
          someone had WITH this employee", which a self-view doesn't
          make sense for). CJ feedback 2026-05-08: this is the UI claim
          defense paper trail. */}
      {viewMode === 'manager' && (
        <CoachingLogCard currentUser={currentUser} targetUserId={profileId} targetName={profile?.name} />
      )}

      {/* Account administration — security (password / kiosk PIN) +
          notification preferences merged. R45: relocated lower so the
          common-case profile pages aren't dominated by admin toggles. */}
      {viewMode === 'self' && (
        <ProfileSection title="Account administration">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#212121',
                              textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Security
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, color: '#757575' }}>
                    Update the password you use for email login.
                  </div>
                  <button onClick={() => setShowChangePw(true)} className="btn-secondary" style={{ fontSize: 12 }}>
                    Change password
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, color: '#757575' }}>
                    Set the 4-digit PIN you use to sign into the kiosk.
                  </div>
                  <button onClick={() => setShowChangePin(true)} className="btn-secondary" style={{ fontSize: 12 }}>
                    Change kiosk PIN
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, color: '#757575' }}>
                    Re-watch the welcome tour that fires on first login.
                  </div>
                  <button onClick={async () => {
                      try {
                        await api('POST', '/api/me/tour/reset', {}, { 'x-user-id': String(currentUser.id) });
                        // Reload so the tour fires immediately. Tracking flag
                        // in URL hash keeps it from re-fire-on-mount-loop.
                        window.location.reload();
                      } catch (e) { alert(e.message || 'Could not reset tour'); }
                    }} className="btn-secondary" style={{ fontSize: 12 }}>
                    Replay welcome tour
                  </button>
                </div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#212121',
                              textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Notifications
              </div>
              <NotificationPrefs profile={profile} currentUser={currentUser} onChanged={load} />
            </div>
          </div>
        </ProfileSection>
      )}
      {viewMode === 'manager' && (
        <ProfileSection title="Account administration">
          <AdminCredentialReset currentUser={currentUser} target={profile} />
          {currentUser.id !== profileId && profile && (
            <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 14, paddingTop: 14,
                          display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <div style={{ fontSize: 13, color: '#555', flex: '1 1 auto', minWidth: 180 }}>
                {profile.status === 'archived'
                  ? 'This user is archived. Restore re-activates them and clears their last day, so they come back onto the schedule in one step; permanent delete frees the email.'
                  : 'Archive hides this user from rosters, chat recipients, and payroll. Restore later from Show archived on the Team list.'}
              </div>
              {profile.status === 'archived' ? (
                <>
                  <button onClick={async () => {
                    try {
                      await api('PUT', `/api/admin/users/${profileId}/restore`, {}, headers);
                      load();
                    } catch (e) { alert(e.message || 'Restore failed'); }
                  }}
                    title="Re-activate this person"
                    style={{ fontSize: 12, fontWeight: 600, padding: '6px 14px',
                              background: 'var(--forest)', color: '#fff', border: 'none',
                              borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Restore user
                  </button>
                  {window.EmpireRoles.isOwner(currentUser) && (
                    <button onClick={async () => {
                      if (!confirm(
                        `Permanently delete ${profile.name}? Their history (audit trail, chat, payroll) is kept ` +
                        `for the record, but the email "${profile.email || '(none)'}" will be freed up so it can be ` +
                        `used for a new signup. This cannot be undone.`
                      )) return;
                      try {
                        await api('DELETE', `/api/admin/users/${profileId}`, null, headers);
                        onClose();
                      } catch (e) { alert(e.message || 'Delete failed'); }
                    }} style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6,
                                 border: '1px solid #c62828', color: '#c62828', background: '#fff',
                                 cursor: 'pointer' }}>
                      Permanently delete
                    </button>
                  )}
                </>
              ) : (
                <button onClick={async () => {
                  if (!confirm(
                    `Archive ${profile.name}? They'll be hidden from rosters, chat recipients, and payroll. ` +
                    `You can restore later.`
                  )) return;
                  try {
                    await api('PUT', `/api/admin/users/${profileId}/archive`, {}, headers);
                    load();
                  } catch (e) { alert(e.message || 'Archive failed'); }
                }} style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6,
                             border: '1px solid #c62828', color: '#c62828', background: '#fff',
                             cursor: 'pointer' }}>
                  Archive user
                </button>
              )}
            </div>
          )}
        </ProfileSection>
      )}
      {showChangePin && (
        <ChangePinModal currentUser={currentUser} onClose={() => setShowChangePin(false)} />
      )}
      {showChangePw && (
        <ChangePasswordModal user={currentUser} onClose={() => setShowChangePw(false)} />
      )}

      {/* Reliability score (Round 20) — replaces Round 8 placeholder. */}
      {viewMode !== 'public' && (
        <ReliabilityScoreCard currentUser={currentUser} targetUserId={profileId} />
      )}

      {/* Team activity — manager-view-only, moved to the bottom (CJ 2026-07-30).
          Just last login now that chat is retired. */}
      {viewMode === 'manager' && activity && (
        <ProfileSection title="Team activity" canEdit={false}>
          <ActivityRows activity={activity} />
        </ProfileSection>
      )}

      {viewMode === 'public' && (
        <div className="card" style={{ fontSize: 12, color: '#757575' }}>
          Contact info only. Reach out for shift coverage.
        </div>
      )}

      {/* CJ 2026-07-29: chat is deprecated (Square is the system of record), so
          the send-message shortcut is removed from the profile. */}
    </div>
  );
}
